import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiCheck, FiChevronDown, FiClock, FiHelpCircle } from 'react-icons/fi';
import ChainIcon from '../ChainIcon';
import CopyButton from './CopyButton';
import TxDetailsPageHeader from './TxDetailsPageHeader';
import SponsoredSection from '../SponsoredSection';
import { formatRelativeTime, formatUtcTimestamp } from '../../utils/formatTime';

function AddressValue({ address, explorerAddressUrl }) {
  if (!address) {
    return <span>—</span>;
  }

  const href = explorerAddressUrl ? `${explorerAddressUrl}${address}` : `/tx/${encodeURIComponent(address)}`;

  if (explorerAddressUrl?.startsWith('http')) {
    return (
      <span className="eth-tx-addr-wrap">
        <a href={href} target="_blank" rel="noreferrer" className="eth-tx-link">
          {address}
        </a>
        <CopyButton text={address} className="eth-tx-copy" />
      </span>
    );
  }

  return (
    <span className="eth-tx-addr-wrap">
      <Link to={href} className="eth-tx-link">
        {address}
      </Link>
      <CopyButton text={address} className="eth-tx-copy" />
    </span>
  );
}

function DetailRow({ label, children, hint }) {
  return (
    <div className="eth-tx-row">
      <div className="eth-tx-row__label">
        <span className="eth-tx-hint" title={hint || label}>
          <FiHelpCircle aria-hidden />
        </span>
        {label}
      </div>
      <div className="eth-tx-row__value">{children}</div>
    </div>
  );
}

function EtherscanStyleTxDetails({ display, loading, error, title = 'Transaction Details' }) {
  if (loading) {
    return (
      <main className="sections eth-tx-page">
        <TxDetailsPageHeader title={title} />
        <div className="stx-skeleton stx-skeleton-panel" />
        <div className="stx-skeleton stx-skeleton-panel" />
      </main>
    );
  }

  if (error || !display) {
    return (
      <main className="sections eth-tx-page">
        <TxDetailsPageHeader title={title} />
        <div className="stx-error">
          <p>{error || 'Transaction not found.'}</p>
          <Link to="/" className="eth-tx-link">
            Back to home
          </Link>
        </div>
      </main>
    );
  }

  const isSuccess = display.status === 'success';
  const isPending = display.status === 'pending';
  const statusTone = isSuccess ? 'ok' : isPending ? 'pending' : 'fail';

  return (
    <main className="sections eth-tx-page">
      <TxDetailsPageHeader title={title} />

      <section className="eth-tx-action-card">
        <p className="eth-tx-action-card__title">{display.actionTitle || 'TRANSACTION ACTION'}</p>
        <div className="eth-tx-action-card__body">
          <ChainIcon chain={display.chain} size="md" />
          <span className={`eth-tx-action-icon${isSuccess ? ' eth-tx-action-icon--ok' : ''}`}>
            <FiArrowUpRight aria-hidden />
          </span>
          <p className="eth-tx-action-text">{display.actionText}</p>
        </div>
      </section>

      <section className="eth-tx-card">
        <DetailRow label="Transaction Hash:" hint="Unique transaction identifier">
          <span className="eth-tx-hash-wrap">
            <code className="eth-tx-mono">{display.hash}</code>
            <CopyButton text={display.hash} className="eth-tx-copy" />
          </span>
        </DetailRow>

        <DetailRow label="Status:">
          <span className={`eth-tx-status eth-tx-status--${statusTone}`}>
            {isSuccess ? <FiCheck aria-hidden /> : isPending ? <FiClock aria-hidden /> : null}
            {display.statusLabel || (isSuccess ? 'Success' : isPending ? 'Pending' : 'Failed')}
          </span>
        </DetailRow>

        <DetailRow label="Block:">
          <span className="eth-tx-inline">
            {display.block != null ? (
              <Link to={`/tx/${display.block}`} className="eth-tx-link eth-tx-link--bold">
                {display.block}
              </Link>
            ) : (
              <span className="eth-tx-pending">Pending</span>
            )}
            {display.confirmationsLabel ? (
              <span className="eth-tx-confirm-pill">{display.confirmationsLabel}</span>
            ) : null}
          </span>
        </DetailRow>

        <DetailRow label="Timestamp:">
          <span className="eth-tx-inline eth-tx-time-row">
            <FiClock aria-hidden />
            <span>{formatRelativeTime(display.blockTime)}</span>
            <span className="eth-tx-time-sep">|</span>
            <span>{formatUtcTimestamp(display.blockTime)}</span>
            <FiChevronDown className="eth-tx-chevron" aria-hidden />
          </span>
        </DetailRow>

        <DetailRow label="Sponsored:">
          <SponsoredSection compact className="sponsored-section--eth-row" />
        </DetailRow>

        <div className="eth-tx-divider" />

        <DetailRow label="From:">
          <AddressValue address={display.from} explorerAddressUrl={display.explorerAddressUrl} />
        </DetailRow>

        <DetailRow label="To:">
          <AddressValue address={display.to} explorerAddressUrl={display.explorerAddressUrl} />
        </DetailRow>

        <div className="eth-tx-divider" />

        {display.value?.amount ? (
          <DetailRow label="Value:">
            <span className="eth-tx-value-row">
              <ChainIcon chain={display.chain} size="md" />
              {display.value.amount}
            </span>
          </DetailRow>
        ) : null}

        <DetailRow label="Transaction Fee:">
          <span>{display.fee?.amount || '—'}</span>
        </DetailRow>

        {display.chain !== 'prc' ? (
          <DetailRow label={`${display.gasPrice?.label || 'Gas Price'}:`}>
            <span>
              {display.gasPrice?.gwei ? `${display.gasPrice.gwei} ` : ''}
              {display.gasPrice?.native && display.gasPrice?.gwei ? (
                <span className="eth-tx-muted">({display.gasPrice.native})</span>
              ) : (
                display.gasPrice?.native || display.gasPrice?.gwei || '—'
              )}
            </span>
          </DetailRow>
        ) : null}
      </section>

      {display.explorerTxUrl ? (
        <p className="eth-tx-external">
          <a href={display.explorerTxUrl} target="_blank" rel="noreferrer" className="eth-tx-link">
            View on {display.chainLabel} explorer
          </a>
        </p>
      ) : null}
    </main>
  );
}

export default EtherscanStyleTxDetails;
